import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Hash, Warning, ArrowRight } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { api, getProviderName, getNameSlug } from "../lib/api";

const SearchByNPI = () => {
  const { npi } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAndRedirect = async () => {
      if (!npi || npi.length !== 10) {
        setError("Invalid NPI number. NPI must be exactly 10 digits.");
        setLoading(false);
        return;
      }

      try {
        const data = await api.getProvider(npi);
        if (data.result) {
          // Redirect to the provider profile page
          const name = getProviderName(data.result);
          const slug = getNameSlug(name);
          navigate(`/provider/${npi}/${slug}`, { replace: true });
        } else {
          setError("No provider found with this NPI number.");
        }
      } catch (err) {
        setError("No provider found with NPI " + npi + ". Please verify the number and try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchAndRedirect();
  }, [npi, navigate]);

  const pageTitle = "NPI Number Lookup " + npi + " — NPPES Registry Search | NPI Lookup";
  const pageDescription = "NPI number " + npi + " lookup — find the healthcare provider assigned to this NPI number in the NPPES national registry and verify credentials.";

  if (loading) {
    return (
      <>
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="description" content={pageDescription} />
        </Helmet>
        
        <div className="bg-slate-50 min-h-screen">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
              <div className="w-16 h-16 bg-teal/10 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                <Hash className="w-8 h-8 text-teal" />
              </div>
              <h1 className="font-display font-bold text-2xl text-slate-900 mb-2">
                Looking up NPI {npi}
              </h1>
              <p className="text-slate-500">
                Fetching provider information...
              </p>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Helmet>
          <title>{"NPI " + npi + " Not Found — NPI Number Lookup | NPI Lookup"}</title>
          <meta name="description" content={"No provider found for NPI number " + npi + ". Check the 10-digit number or search the national NPPES NPI registry by name or specialty."} />
        </Helmet>
        
        <div className="bg-slate-50 min-h-screen">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Warning className="w-8 h-8 text-red-500" />
              </div>
              <h1 className="font-display font-bold text-2xl text-slate-900 mb-2">
                NPI Not Found
              </h1>
              <p className="text-slate-500 mb-6">
                {error}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/">
                  <Button className="bg-navy hover:bg-navy/90">
                    Search Again
                  </Button>
                </Link>
                <a 
                  href="https://npiregistry.cms.hhs.gov/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="outline" className="gap-2">
                    Try Official Registry
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return null;
};

export default SearchByNPI;
